import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { MonthlyCollectionChart } from "../../components/dashboard/MonthlyCollectionChart";   // Phase 213
import { HeadWiseSummaryCard } from "../../components/HeadWiseReport";   // Phase 230
import { Home, IndianRupee, Wallet, AlertCircle, BadgeCheck, Database, RefreshCw, MousePointerClick } from "lucide-react";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";
import { KpiCard, PageHeader } from "../../components/common";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Button } from "../../components/ui/button";
import { Skeleton } from "../../components/ui/skeleton";
import { api, fmtINR } from "../../lib/api";
import { useLiveCollections } from "../../lib/liveCollections";
import { LiveBadge } from "../../components/LiveBadge";
import { CoverageAlertsCard } from "../../components/CoverageAlertsCard";
import { useRoleModules } from "../../lib/roleModules";
import { useAuth } from "../../context/AuthContext";

const COLORS = ["hsl(206,88%,28%)", "hsl(152,55%,32%)", "hsl(38,92%,45%)", "hsl(199,89%,40%)", "hsl(0,72%,50%)", "hsl(260,50%,50%)", "hsl(180,50%,35%)"];

export const SOURCE_LABELS = {
  excel_upload: "Excel Upload", third_party: "Third Party", manual_entry: "Manual Entry",
  new_survey: "New Survey", reassessment: "Reassessment", citizen: "Citizen", unknown: "Unknown",
};
export const sourceLabel = (s) => SOURCE_LABELS[s] || (s || "Unknown").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

/**
 * Phase 83: the dashboard paints instantly and fills in as data arrives.
 * `summary` (KPIs + pending counters, index-backed counts) and `charts` (heavier
 * aggregations) are fetched independently, so a slow aggregation never blocks the
 * first render or the KPI tiles. Each section has its own skeleton + retry.
 */
const useSection = (path) => {
  const [state, setState] = useState({ data: null, loading: true, error: null });
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true; // re-armed on (Strict Mode) remount
    return () => { alive.current = false; };
  }, []);
  const load = useCallback(async (opts = {}) => {
    // Phase 149: `silent` keeps the current figures on screen (no skeleton) while fresh ones arrive
    setState((s) => ({ ...s, loading: !(opts.silent && s.data), error: null }));
    try {
      const r = await api.get(path, { params: opts.refresh ? { refresh: 1 } : undefined });
      if (alive.current) setState({ data: r.data, loading: false, error: null });
    } catch (e) {
      if (alive.current) setState((s) => ({ ...s, loading: false, error: e }));
    }
  }, [path]);
  useEffect(() => { load(); }, [load]);
  return { ...state, reload: load };
};

const SectionError = ({ onRetry, testId }) => (
  <div className="flex flex-col items-center justify-center gap-2 h-full py-8 text-center" data-testid={testId}>
    <AlertCircle className="h-5 w-5 text-destructive" />
    <p className="text-sm text-muted-foreground">Could not load this section.</p>
    <Button size="sm" variant="outline" onClick={onRetry} data-testid={`${testId}-retry`}>
      <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Retry
    </Button>
  </div>
);

const KpiSkeleton = () => (
  <Card className="shadow-sm"><CardContent className="p-4 space-y-3">
    <div className="flex items-center justify-between"><Skeleton className="h-3 w-24" /><Skeleton className="h-8 w-8 rounded-full" /></div>
    <Skeleton className="h-7 w-32" />
  </CardContent></Card>
);

const ChartSkeleton = ({ bars = 6 }) => (
  <div className="h-full flex items-end gap-3 px-4 pb-6 pt-4" aria-hidden="true">
    {Array.from({ length: bars }).map((_, i) => (
      <Skeleton key={i} className="flex-1 rounded-t-md" style={{ height: `${35 + ((i * 37) % 55)}%` }} />
    ))}
  </div>
);

/** Phase 84: small affordance telling admins the chart is a drilldown entry point. */
const DrillHint = ({ children = "Click to drill down" }) => (
  <span className="ml-auto inline-flex items-center gap-1 text-[11px] font-normal text-muted-foreground" aria-hidden="true">
    <MousePointerClick className="h-3 w-3" /> {children}
  </span>
);

const qs = (params) => {
  const p = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => { if (v) p.set(k, v); });
  return p.toString();
};

export default function AdminDashboard() {
  const { code } = useParams();
  const navigate = useNavigate();
  const summary = useSection(`/t/${code}/dashboard/admin/summary`);
  const charts = useSection(`/t/${code}/dashboard/admin/charts`);
  // Phase 156: the coverage alerts card follows the Reports → Unassigned Wards feature access
  const access = useRoleModules(code);
  const showCoverage = access.featureAllowed("reports", "unassigned_wards");
  // Phase 230: the "Collection by tax head" card follows the Reports → Head-wise Collection feature access
  const showHeadWise = access.featureAllowed("reports", "head_wise");
  // Phase 214: who may set the monthly collection targets drawn on the Monthly Collection graph
  const canEditTargets = access.canWrite("settings", "collection_targets");
  const muniName = useAuth()?.auth?.municipality?.name || "";
  // Phase 149: a payment recorded anywhere (any user, any device) refreshes the KPIs + charts here
  useLiveCollections(code, () => { summary.reload({ silent: true }); charts.reload({ silent: true, refresh: true }); });

  // Drilldowns push a history entry, so Back returns to the dashboard. A chart click can
  // reach us twice (Bar.onClick + chart onClick) — collapse duplicates within 400ms.
  const lastDrill = useRef({ url: "", at: 0 });
  const drill = useCallback((page, params) => {
    const url = `/${code}/admin/${page}?${qs(params)}`;
    const now = Date.now();
    if (lastDrill.current.url === url && now - lastDrill.current.at < 400) return;
    lastDrill.current = { url, at: now };
    navigate(url);
  }, [navigate, code]);
  // Recharts 3 chart-level onClick exposes the active category index/label (no payload):
  // resolve the clicked row ourselves so a click anywhere across the category band works.
  const activeRow = (s, rows, labelKey) => {
    if (!s || !rows?.length) return null;
    const i = Number(s.activeTooltipIndex ?? s.activeIndex);
    if (Number.isInteger(i) && rows[i]) return rows[i];
    return s.activeLabel != null ? rows.find((r) => r[labelKey] === s.activeLabel) : null;
  };
  const onTrendClick = (s) => { const d = s?.activeLabel ?? activeRow(s, charts.data?.collection_trend, "date")?.date; if (d) drill("collections", { date_from: d, date_to: d }); };
  const onModeClick = (d) => { const m = d?.payload?.mode ?? d?.name; if (m && m !== "Unknown") drill("collections", { mode: m }); };
  const sourceRows = (charts.data?.revenue_by_source || []).map((s) => ({ ...s, label: sourceLabel(s.source) }));
  const onSourceClick = (src) => { if (src && src !== "unknown") drill("properties", { source: src }); };
  const onSourceChartClick = (s) => onSourceClick(activeRow(s, sourceRows, "label")?.source);
  const onSourceBarClick = (d) => onSourceClick(d?.payload?.source);
  const wardDrill = (p) => { if (p?.ward_id) drill("properties", { zone_id: p.zone_id, ward_id: p.ward_id }); };
  const onWardClick = (s) => wardDrill(activeRow(s, charts.data?.ward_wise, "ward"));
  const onWardBarClick = (d) => wardDrill(d?.payload);
  const modeTotal = (charts.data?.payment_modes || []).reduce((a, m) => a + (m.amount || 0), 0);

  const k = summary.data?.kpis;
  const pct = k && k.total_demand > 0 ? Math.round((k.collected / k.total_demand) * 100) : 0;
  const propsCollectedPct = k && k.total_properties > 0
    ? Math.round(((k.properties_collected || 0) / k.total_properties) * 100) : 0;
  const refreshing = summary.loading || charts.loading;
  const refreshAll = () => { summary.reload(); charts.reload({ refresh: true }); };
  const generatedAt = charts.data?.generated_at ? new Date(charts.data.generated_at) : null;

  const pendingTiles = [
    { label: "Pending Reassessments", key: "reassessments", to: `/${code}/admin/approvals`, testId: "pending-reassessments" },
    { label: "Pending Surveys", key: "surveys", to: `/${code}/admin/approvals`, testId: "pending-surveys" },
    { label: "Open Grievances", key: "grievances", to: `/${code}/admin/grievances`, testId: "pending-grievances" },
    { label: "Open Applications", key: "applications", to: `/${code}/admin/applications`, testId: "pending-applications" },
  ];

  return (
    <div data-testid="admin-dashboard">
      <PageHeader title="Dashboard"
        subtitle={k ? `Revenue overview • ${pct}% of demand collected` : "Revenue overview"}>
        <LiveBadge />
        {generatedAt && (
          <span className="text-xs text-muted-foreground tabular-nums" data-testid="dashboard-charts-updated">
            Charts updated {generatedAt.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
          </span>
        )}
        <Button variant="outline" size="sm" onClick={refreshAll} disabled={refreshing} data-testid="dashboard-refresh-button">
          <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${refreshing ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </PageHeader>

      {/* KPIs — summary call */}
      <div className="grid grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5 gap-4 mb-6" data-testid="kpi-grid">
        {summary.error && !k ? (
          <Card className="shadow-sm col-span-2 lg:col-span-3 2xl:col-span-5"><CardContent className="p-2">
            <SectionError onRetry={summary.reload} testId="kpi-error" />
          </CardContent></Card>
        ) : !k ? (
          <>{[0, 1, 2, 3, 4].map((i) => <KpiSkeleton key={i} />)}</>
        ) : (
          <>
            <KpiCard label="Total Properties" value={k.total_properties} icon={Home} testId="kpi-properties" />
            <KpiCard label="Total Demand" value={k.total_demand} money icon={IndianRupee} testId="kpi-total-demand" />
            <KpiCard label="Collected" value={k.collected} money icon={Wallet} testId="kpi-collected"
              sub={`${pct}% realized · ${(k.receipts || 0).toLocaleString("en-IN")} receipts (all sources)${k.citizen_portal_collected > 0 ? ` · ${fmtINR(k.citizen_portal_collected)} via Citizen Portal` : ""}`} />
            <KpiCard label="Properties Collected" value={k.properties_collected ?? 0}
              sub={`${propsCollectedPct}% of all properties`} icon={BadgeCheck} testId="kpi-properties-collected" />
            <KpiCard label="Balance Due" value={k.balance} money icon={AlertCircle} testId="kpi-balance" />
          </>
        )}
      </div>

      {/* Pending counters — summary call */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="pending-grid">
        {pendingTiles.map((x) => {
          const v = summary.data?.pending?.[x.key];
          return (
            <Link to={x.to} key={x.label} data-testid={x.testId}>
              <Card className="shadow-sm hover:shadow-md transition-shadow h-full">
                <CardContent className="p-4 flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">{x.label}</p>
                  {v == null ? <Skeleton className="h-6 w-8" /> : (
                    <p className={`text-xl font-semibold tabular-nums ${v > 0 ? "text-amber-600" : ""}`}>{v}</p>
                  )}
                </CardContent>
              </Card>
            </Link>
          );
        })}
      </div>

      {/* Phase 156: territory coverage alerts — stranded properties nobody can see */}
      {showCoverage && <CoverageAlertsCard code={code} className="mb-6" />}

      {/* Phase 213: Monthly Collection live graph — selected FY vs previous FY, auto-refresh 60 s */}
      <MonthlyCollectionChart code={code} className="mb-6" testIdPrefix="admin-monthly-collection" canEditTargets={canEditTargets}
        municipalityName={muniName ? `${muniName} (${(code || "").toUpperCase()})` : ""} />

      {/* Phase 230: collected vs outstanding per tax head (current FY) — opens Reports → Head-wise Collection */}
      {showHeadWise && <HeadWiseSummaryCard code={code} href={`/${code}/admin/head-wise`} className="mb-6" />}

      {/* Charts — charts call (background) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <Card className="shadow-sm" data-testid="collection-trend-card">
          <CardHeader className="pb-2"><CardTitle className="text-base font-heading flex items-center">Collection trend <DrillHint>Click a day to see its receipts</DrillHint></CardTitle></CardHeader>
          <CardContent className="h-64">
            {charts.error ? <SectionError onRetry={charts.reload} testId="charts-error" /> :
              !charts.data ? <ChartSkeleton bars={10} /> :
              charts.data.collection_trend.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center pt-20">No payments recorded yet</p>
              ) : (
                <div className="h-full cursor-pointer" data-testid="collection-trend-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={charts.data.collection_trend} onClick={onTrendClick}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e9ef" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v) => fmtINR(v)} />
                    <Area type="monotone" dataKey="amount" stroke="hsl(206,88%,28%)" fill="hsl(206,88%,28%)" fillOpacity={0.15} activeDot={{ r: 5 }} />
                  </AreaChart>
                </ResponsiveContainer>
                </div>
              )}
          </CardContent>
        </Card>
        <Card className="shadow-sm" data-testid="payment-modes-card">
          <CardHeader className="pb-2"><CardTitle className="text-base font-heading flex items-center">Payment mode split <DrillHint>Click a slice or mode to list receipts</DrillHint></CardTitle></CardHeader>
          <CardContent className="sm:h-64">
            {charts.error ? <SectionError onRetry={charts.reload} testId="modes-error" /> :
              !charts.data ? (
                <div className="h-56 sm:h-full flex items-center justify-center" aria-hidden="true"><Skeleton className="h-40 w-40 rounded-full" /></div>
              ) : charts.data.payment_modes.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center pt-20">No payments recorded yet</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 h-full">
                  <div className="h-56 sm:h-full cursor-pointer" data-testid="payment-modes-chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={charts.data.payment_modes} dataKey="amount" nameKey="mode" innerRadius={50} outerRadius={80} paddingAngle={2} onClick={onModeClick}>
                          {charts.data.payment_modes.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                        </Pie>
                        <Tooltip formatter={(v) => fmtINR(v)} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  {/* Accessible, always-hittable drilldown targets mirroring the slices */}
                  <ul className="flex flex-col justify-center gap-1 overflow-y-auto" data-testid="payment-modes-list" aria-label="Payment modes">
                    {charts.data.payment_modes.map((m, i) => (
                      <li key={m.mode}>
                        <button type="button" onClick={() => onModeClick({ name: m.mode })} disabled={m.mode === "Unknown"}
                          className="w-full flex items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-muted/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                          data-testid={`mode-drill-${m.mode}`} title={m.mode === "Unknown" ? undefined : `Open ${m.mode} receipts`}>
                          <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: COLORS[i % COLORS.length] }} aria-hidden="true" />
                          <span className="font-medium truncate">{m.mode}</span>
                          <span className="ml-auto tabular-nums text-muted-foreground text-xs">{m.count} rcpt</span>
                          <span className="tabular-nums font-medium w-24 text-right">{fmtINR(m.amount)}</span>
                          <span className="tabular-nums text-muted-foreground text-xs w-10 text-right">{modeTotal > 0 ? `${Math.round((m.amount / modeTotal) * 100)}%` : "—"}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
          </CardContent>
        </Card>
      </div>

      <Card className="shadow-sm mb-6" data-testid="revenue-by-source-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-heading flex items-center gap-2">
            <Database className="h-4 w-4 text-muted-foreground" /> Revenue by data source <DrillHint>Click a source to open its properties</DrillHint>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {charts.error ? <SectionError onRetry={charts.reload} testId="source-error" /> :
            !charts.data ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="h-64"><ChartSkeleton bars={5} /></div>
                <div className="space-y-2 pt-2">{[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-9 w-full" />)}</div>
              </div>
            ) : (!charts.data.revenue_by_source || charts.data.revenue_by_source.length === 0) ? (
              <p className="text-sm text-muted-foreground text-center py-10">No demand data yet</p>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="h-64 cursor-pointer" data-testid="revenue-by-source-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={sourceRows} onClick={onSourceChartClick}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e9ef" />
                      <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={0} angle={-12} textAnchor="end" height={44} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip formatter={(v) => fmtINR(v)} cursor={{ fill: "hsl(206,88%,28%)", fillOpacity: 0.06 }} />
                      <Legend />
                      <Bar dataKey="demand" name="Demand" fill="hsl(206,30%,75%)" radius={[4, 4, 0, 0]} onClick={onSourceBarClick} />
                      <Bar dataKey="collected" name="Collected" fill="hsl(152,55%,32%)" radius={[4, 4, 0, 0]} onClick={onSourceBarClick} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="overflow-x-auto">
                  <Table data-testid="revenue-by-source-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Source</TableHead>
                        <TableHead className="text-right">Demand</TableHead>
                        <TableHead className="text-right">Collected</TableHead>
                        <TableHead className="text-right">Share</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(() => {
                        const rows = charts.data.revenue_by_source;
                        const totalCollected = rows.reduce((a, s) => a + (s.collected || 0), 0);
                        return rows.map((s) => (
                          <TableRow key={s.source} data-testid={`revenue-source-row-${s.source}`}
                            className={s.source !== "unknown" ? "cursor-pointer hover:bg-muted/60" : ""}
                            onClick={() => onSourceClick(s.source)} title={s.source !== "unknown" ? "Open these properties" : undefined}>
                            <TableCell className="font-medium">{sourceLabel(s.source)}</TableCell>
                            <TableCell className="text-right tabular-nums">{fmtINR(s.demand)}</TableCell>
                            <TableCell className="text-right tabular-nums font-medium">{fmtINR(s.collected)}</TableCell>
                            <TableCell className="text-right tabular-nums text-muted-foreground">
                              {totalCollected > 0 ? `${Math.round((s.collected / totalCollected) * 100)}%` : "—"}
                            </TableCell>
                          </TableRow>
                        ));
                      })()}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
        </CardContent>
      </Card>

      <Card className="shadow-sm" data-testid="ward-wise-card">
        <CardHeader className="pb-2"><CardTitle className="text-base font-heading flex items-center">Ward-wise demand vs collection <DrillHint>Click a ward to open its properties</DrillHint></CardTitle></CardHeader>
        <CardContent>
          {charts.error ? <SectionError onRetry={charts.reload} testId="ward-error" /> :
            !charts.data ? (
              <div className="h-72 flex flex-col justify-center gap-3 px-4" aria-hidden="true">
                {[0, 1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-5" style={{ width: `${90 - i * 11}%` }} />)}
              </div>
            ) : charts.data.ward_wise.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-24">No ward-level demand data yet</p>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="h-72 cursor-pointer" data-testid="ward-wise-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={charts.data.ward_wise} layout="vertical" margin={{ left: 10 }} onClick={onWardClick}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e9ef" />
                      <XAxis type="number" tick={{ fontSize: 11 }} />
                      <YAxis type="category" dataKey="ward" width={70} tick={{ fontSize: 11 }} />
                      <Tooltip formatter={(v) => fmtINR(v)} cursor={{ fill: "hsl(206,88%,28%)", fillOpacity: 0.06 }} />
                      <Legend />
                      <Bar dataKey="total" name="Demand" fill="hsl(206,30%,75%)" radius={[0, 4, 4, 0]} onClick={onWardBarClick} />
                      <Bar dataKey="collected" name="Collected" fill="hsl(152,55%,32%)" radius={[0, 4, 4, 0]} onClick={onWardBarClick} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                {/* Accessible, always-hittable drilldown targets mirroring the bars */}
                <div className="overflow-auto max-h-72 rounded-md border">
                  <Table data-testid="ward-wise-table">
                    <TableHeader className="sticky top-0 bg-card z-10">
                      <TableRow>
                        <TableHead>Ward</TableHead>
                        <TableHead className="text-right">Demand</TableHead>
                        <TableHead className="text-right">Collected</TableHead>
                        <TableHead className="text-right">Realised</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {charts.data.ward_wise.map((w) => (
                        <TableRow key={w.ward_id || w.ward} className="cursor-pointer hover:bg-muted/60" onClick={() => wardDrill(w)}
                          tabIndex={0} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); wardDrill(w); } }}
                          data-testid={`ward-row-${w.ward_id || w.ward}`} title={`Open ${w.ward} properties`}>
                          <TableCell className="font-medium">{w.ward}</TableCell>
                          <TableCell className="text-right tabular-nums">{fmtINR(w.total)}</TableCell>
                          <TableCell className="text-right tabular-nums font-medium">{fmtINR(w.collected)}</TableCell>
                          <TableCell className="text-right tabular-nums text-muted-foreground">
                            {w.total > 0 ? `${Math.round((w.collected / w.total) * 100)}%` : "—"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
        </CardContent>
      </Card>
    </div>
  );
}
